import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search, X, Plus, Trash2, Pencil, Upload, Link2, Star, Check, ArrowRightLeft, ExternalLink,
} from 'lucide-react';
import { db, storage } from '../firebase';
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, updateDoc, addDoc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';
import { FIELD_CLASS, LABEL_CLASS, FilterSelect } from '../components/crm/CrmFilterUi';
import { CORE_LINKS_FIELD, coreLinkOptionId, getCoreToolLinks, resolveCoreToolPick } from '../utils/techStackCoreLinks';
import { compareTechSoftware, compareTechTypes, fetchTechTierMeta, TECH_TIERS } from '../utils/techStackTiers';

const TIERS = ['Core', 'Primary', 'Secondary'];

// Spelled-out tone classes (no dynamic Tailwind strings — JIT would drop them).
const TIER_META = {
  Core: {
    emoji: '🧱',
    blurb: 'The foundation every client runs on',
    chip: 'bg-red-100/80 dark:bg-red-900/40 ring-red-200 dark:ring-red-800/60',
    active: 'bg-red-600 text-white shadow-[0_8px_24px_rgba(220,38,38,0.35)]',
    count: 'bg-white/25 text-white',
  },
  Primary: {
    emoji: '🧩',
    blurb: 'Day-to-day operating tools',
    chip: 'bg-orange-100/80 dark:bg-orange-900/40 ring-orange-200 dark:ring-orange-800/60',
    active: 'bg-orange-500 text-white shadow-[0_8px_24px_rgba(249,115,22,0.35)]',
    count: 'bg-white/25 text-white',
  },
  Secondary: {
    emoji: '🛠️',
    blurb: 'Specialist and add-on tools',
    chip: 'bg-amber-100/80 dark:bg-amber-900/40 ring-amber-200 dark:ring-amber-800/60',
    active: 'bg-amber-500 text-white shadow-[0_8px_24px_rgba(245,158,11,0.35)]',
    count: 'bg-white/25 text-white',
  },
};

const FILTERS = [
  { id: 'all', label: 'All software' },
  { id: 'suggested', label: '⭐ Suggested' },
  { id: 'open', label: '🔌 Open API' },
  { id: 'core', label: '🔗 Reused from Core' },
];

const GLASS = 'bg-white/60 dark:bg-gray-900/50 backdrop-blur-xl border border-white/70 dark:border-white/10 shadow-[0_8px_30px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.35)]';
const INPUT = `${FIELD_CLASS} px-3 py-2.5`;
const BTN_PRIMARY = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-sm transition';
const BTN_SECONDARY = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm transition';
const BTN_DANGER = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-900/60 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 text-sm font-medium transition';
const ICON_BTN = 'inline-flex items-center justify-center w-9 h-9 rounded-xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-white/80 dark:hover:bg-gray-800 transition';

const pageStyles = `
  @keyframes techstack-rise {
    from { opacity: 0; transform: translateY(14px) scale(0.985); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .techstack-rise { animation: techstack-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) both; }
  @keyframes techstack-pop {
    from { opacity: 0; transform: translateY(10px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .techstack-pop { animation: techstack-pop 0.28s cubic-bezier(0.22, 1, 0.36, 1) both; }
  @media (prefers-reduced-motion: reduce) {
    .techstack-rise, .techstack-pop { animation: none; }
    .techstack-tile { transition: none !important; }
  }
`;

const emptyCollections = () => TIERS.reduce((acc, c) => ({ ...acc, [c]: { name: c, types: [] } }), {});

const normalizeSoftware = (id, data) => ({
  id,
  ...data,
  // Legacy docs stored useCases[] instead of index ids
  useCaseIds: (data.useCaseIds || (data.useCases ? data.useCases.map((_, i) => String(i)) : [])).map(String),
  suggested: !!data.suggested,
  open: !!data.open,
  connectionFields: data.connectionFields || [],
});

// What Firestore stores for a link (getCoreLinks normalises on read).
const linkDoc = (link) => ({
  coreTypeId: link.coreTypeId,
  useCaseIds: (link.useCaseIds || []).map(String),
  suggested: !!link.suggested,
});

const patchType = (prev, c, typeId, fn) => ({
  ...prev,
  [c]: { ...prev[c], types: prev[c].types.map((t) => (t.id === typeId ? fn(t) : t)) },
});

// Client picks are keyed by tier + tool type, so a tool type moving tiers takes
// them along: its picks and proposals, plus apps added beside it with the "+".
// Covers accounts/{id}.techStack and the legacy users/{uid}.techStack.
const moveClientSelections = async (typeId, fromTier, toTier) => {
  const from = fromTier.toLowerCase();
  const to = toTier.toLowerCase();
  const belongs = (item) => item?.categoryId === from && (item.toolId === typeId || (item.addedApp && item.baseToolId === typeId));
  const writes = [];
  for (const name of ['accounts', 'users']) {
    const snap = await getDocs(collection(db, name));
    snap.docs.forEach((d) => {
      const techStack = d.data()?.techStack || {};
      const patch = {};
      ['selectedSoftware', 'proposedSoftware'].forEach((field) => {
        const list = Array.isArray(techStack[field]) ? techStack[field] : [];
        if (list.some(belongs)) patch[`techStack.${field}`] = list.map((item) => (belongs(item) ? { ...item, categoryId: to } : item));
      });
      if (Object.keys(patch).length) writes.push([d.ref, patch]);
    });
  }
  for (let i = 0; i < writes.length; i += 400) {
    const batch = writeBatch(db);
    writes.slice(i, i + 400).forEach(([docRef, patch]) => batch.update(docRef, patch));
    await batch.commit();
  }
  return writes.length;
};

const emptySoftwareDraft = (collectionName = 'Core', typeId = '') => ({
  name: '',
  image: '',
  signupUrl: '',
  collection: collectionName,
  typeId,
  useCaseIds: [],
  suggested: false,
  open: false,
  connectionFields: [],
});

/* ── Small building blocks ─────────────────────────────────────────────────── */

const SoftwareLogo = ({ src, name, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const box = size === 'lg' ? 'w-24 h-24 rounded-2xl text-3xl' : size === 'xs' ? 'w-5 h-5 rounded-full text-[10px]' : size === 'sm' ? 'w-9 h-9 rounded-lg text-sm' : 'w-14 h-14 rounded-xl text-lg';
  if (src && !failed) {
    return (
      <div className={`${box} shrink-0 overflow-hidden bg-white ring-1 ring-gray-200/80 dark:ring-white/10 shadow-sm`}>
        <img src={src} alt={name || 'Software logo'} className="w-full h-full object-contain" onError={() => setFailed(true)} />
      </div>
    );
  }
  return (
    <div className={`${box} shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-200/80 dark:ring-white/10 font-bold text-gray-400 dark:text-gray-500`}>
      {(name || '?').trim().charAt(0).toUpperCase() || '?'}
    </div>
  );
};

const Toggle = ({ checked, onChange, label, hint }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`w-full flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition ${
      checked
        ? 'border-red-200 dark:border-red-900/60 bg-red-50/70 dark:bg-red-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-800'
    }`}
  >
    <span>
      <span className="block text-sm font-medium text-gray-900 dark:text-white">{label}</span>
      {hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</span>}
    </span>
    <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </span>
  </button>
);

const UseCasePills = ({ useCases, selected, onToggle }) => {
  if (!useCases.length) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">This tool type has no use cases yet — add them from the tool type's edit menu.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {useCases.map((useCase, index) => {
        const id = String(index);
        const on = selected.includes(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              on
                ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                : 'border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            {on && <Check className="w-3 h-3" />}
            {useCase}
          </button>
        );
      })}
    </div>
  );
};

const Modal = ({ emoji, title, subtitle, onClose, children, footer, width = 'max-w-2xl' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onMouseDown={onClose}>
    <div
      className={`techstack-pop relative w-full ${width} max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button type="button" onClick={onClose} className={`absolute top-3 right-3 ${ICON_BTN}`} aria-label="Close">
        <X className="w-5 h-5" />
      </button>
      <div className="px-6 pt-6 pb-4 text-center border-b border-gray-100 dark:border-gray-800">
        {emoji && <div className="text-3xl mb-2" aria-hidden="true">{emoji}</div>}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      {footer && <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800">{footer}</div>}
    </div>
  </div>
);

const Badge = ({ tone, children, title }) => {
  const tones = {
    star: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800/60',
    open: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60',
    core: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60',
  };
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

/* ── Page ──────────────────────────────────────────────────────────────────── */

const TechStackTemplate = () => {
  const [collections, setCollections] = useState(emptyCollections);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Core');
  // Tier display names + pyramid order (data keys stay the TIERS doc ids)
  const [tierMeta, setTierMeta] = useState(() => TECH_TIERS.map((t) => ({ ...t, title: t.defaultTitle })));
  const tierOrder = tierMeta.map((t) => t.docId);
  const tierTitle = (c) => tierMeta.find((t) => t.docId === c)?.title || c;
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  // { mode: 'add'|'edit', collection, id?, name, description, rows: [{ text, origIndex }] }
  const [typeModal, setTypeModal] = useState(null);
  // { mode: 'add'|'edit', id?, origCollection?, origTypeId?, origUseCaseIds?, draft }
  const [softwareModal, setSoftwareModal] = useState(null);
  const [newField, setNewField] = useState({ fieldName: '', fieldDescription: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // { collection, typeId, link }
  const [linkModal, setLinkModal] = useState(null);
  // { collection, typeId, query, selected: [] }
  const [corePicker, setCorePicker] = useState(null);
  // { title, message, confirmLabel, onConfirm }
  const [confirm, setConfirm] = useState(null);

  const loadTemplate = useCallback(async () => {
    try {
      const tiers = await Promise.all(
        TIERS.map(async (c) => {
          const typesSnap = await getDocs(collection(db, 'technologyStack', c, 'types'));
          const types = await Promise.all(
            typesSnap.docs
              .filter((d) => d.data()?.name)
              .map(async (d) => {
                const data = d.data();
                const swSnap = await getDocs(collection(db, 'technologyStack', c, 'types', d.id, 'softwares'));
                const software = swSnap.docs
                  .filter((s) => s.data()?.name)
                  .map((s) => normalizeSoftware(s.id, s.data()))
                  .sort(compareTechSoftware);
                return {
                  id: d.id,
                  name: data.name,
                  order: data.order,
                  description: data.description || '',
                  useCases: data.useCases || [],
                  coreLinks: getCoreToolLinks(data),
                  software,
                };
              })
          );
          return [c, { name: c, types: types.sort(compareTechTypes) }];
        })
      );
      setTierMeta(await fetchTechTierMeta());
      setCollections(Object.fromEntries(tiers));
    } catch (error) {
      console.error('Error loading tech stack template:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // Core typeId → Core tool type, for resolving links.
  const coreTypesById = useMemo(() => new Map(collections.Core.types.map((type) => [type.id, type])), [collections]);

  // Core typeId → [{ collection, typeId, typeName }] where that Core tool is reused.
  const linkUsage = useMemo(() => {
    const map = new Map();
    ['Primary', 'Secondary'].forEach((c) =>
      collections[c].types.forEach((type) =>
        type.coreLinks.forEach((link) => {
          if (!map.has(link.coreTypeId)) map.set(link.coreTypeId, []);
          map.get(link.coreTypeId).push({ collection: c, typeId: type.id, typeName: type.name });
        })
      )
    );
    return map;
  }, [collections]);

  // A type's own software plus one "Your <Core tool>" option per linked Core tool.
  // On a client's pyramid that option becomes whatever they run for the Core
  // tool; here it previews the suggested software.
  const optionsForType = useCallback(
    (type) => [
      ...type.software,
      ...type.coreLinks
        .map((link) => {
          const coreType = coreTypesById.get(link.coreTypeId);
          if (!coreType) return null;
          const pick = resolveCoreToolPick(coreType, null);
          return {
            id: coreLinkOptionId(link.coreTypeId),
            name: `Your ${coreType.name}`,
            image: pick?.image || '',
            open: !!pick?.open,
            pickName: pick?.name || '',
            useCaseIds: link.useCaseIds,
            suggested: link.suggested,
            fromCore: true,
            linkedCoreTypeId: link.coreTypeId,
            coreSoftware: coreType.software,
          };
        })
        .filter(Boolean),
    ],
    [coreTypesById]
  );

  const findType = (c, typeId) => collections[c]?.types.find((t) => t.id === typeId) || null;

  /* ── Filtering + stats ── */

  const visibleTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return collections[activeTab].types
      .map((type) => {
        const typeMatches = !q || type.name.toLowerCase().includes(q);
        const options = optionsForType(type).filter((sw) => {
          if (filter === 'suggested' && !sw.suggested) return false;
          if (filter === 'open' && !sw.open) return false;
          if (filter === 'core' && !sw.fromCore) return false;
          return typeMatches || sw.name.toLowerCase().includes(q);
        });
        return { type, options, show: (!q && filter === 'all') || options.length > 0 || (typeMatches && filter === 'all') };
      })
      .filter((row) => row.show);
  }, [collections, activeTab, search, filter, optionsForType]);

  const stats = useMemo(() => {
    const types = collections[activeTab].types;
    const options = types.flatMap((type) => optionsForType(type));
    return {
      types: types.length,
      software: options.length,
      suggested: options.filter((sw) => sw.suggested).length,
      open: options.filter((sw) => sw.open).length,
      fromCore: options.filter((sw) => sw.fromCore).length,
    };
  }, [collections, activeTab, optionsForType]);

  const filtersActive = search.trim() !== '' || filter !== 'all';

  /* ── Core link bookkeeping ── */

  // Drop links to a Core tool type that was deleted.
  const stripCoreLinks = async (coreTypeIds, snapshot = collections) => {
    const ids = new Set(coreTypeIds);
    const writes = [];
    let next = snapshot;
    ['Primary', 'Secondary'].forEach((c) =>
      snapshot[c].types.forEach((type) => {
        if (!type.coreLinks.some((l) => ids.has(l.coreTypeId))) return;
        const kept = type.coreLinks.filter((l) => !ids.has(l.coreTypeId));
        writes.push(updateDoc(doc(db, 'technologyStack', c, 'types', type.id), { [CORE_LINKS_FIELD]: kept.map(linkDoc) }));
        next = patchType(next, c, type.id, (t) => ({ ...t, coreLinks: kept }));
      })
    );
    await Promise.all(writes);
    return next;
  };

  const writeLinks = async (c, typeId, links) => {
    await updateDoc(doc(db, 'technologyStack', c, 'types', typeId), { [CORE_LINKS_FIELD]: links.map(linkDoc) });
    setCollections((prev) => patchType(prev, c, typeId, (t) => ({ ...t, coreLinks: links })));
  };

  /* ── Tool types ── */

  const openAddType = () =>
    setTypeModal({ mode: 'add', collection: activeTab, name: '', description: '', rows: [] });

  const openEditType = (c, type) =>
    setTypeModal({
      mode: 'edit',
      collection: c,
      origCollection: c,
      id: type.id,
      name: type.name,
      description: type.description,
      rows: type.useCases.map((text, i) => ({ text, origIndex: i })),
    });

  const saveType = async () => {
    const m = typeModal;
    const name = m.name.trim();
    if (!name) return;
    const rows = m.rows.map((r) => ({ ...r, text: r.text.trim() })).filter((r) => r.text);
    const useCases = rows.map((r) => r.text);
    const description = m.description.trim();
    setSaving(true);
    try {
      if (m.mode === 'add') {
        const typeRef = await addDoc(collection(db, 'technologyStack', m.collection, 'types'), { name, useCases, description });
        setCollections((prev) => ({
          ...prev,
          [m.collection]: {
            ...prev[m.collection],
            types: [...prev[m.collection].types, { id: typeRef.id, name, description, useCases, coreLinks: [], software: [] }].sort(compareTechTypes),
          },
        }));
        setActiveTab(m.collection);
      } else {
        const from = m.origCollection;
        const to = m.collection;
        const type = findType(from, m.id);
        // Software stores use cases as indexes — follow renames, reorders and removals.
        const indexMap = [];
        rows.forEach((r, newIndex) => {
          if (r.origIndex !== null && r.origIndex !== undefined) indexMap[r.origIndex] = String(newIndex);
        });
        const remap = (ids) => [...new Set((ids || []).map((i) => indexMap[parseInt(i, 10)]).filter((i) => i != null))];

        const typeRef = doc(db, 'technologyStack', from, 'types', m.id);
        // Core types can't reuse Core tools, so a type moving into Core drops its links
        const coreLinks = to === 'Core' ? [] : type.coreLinks.map((l) => ({ ...l, useCaseIds: remap(l.useCaseIds) }));
        const software = type.software.map((sw) => ({ ...sw, useCaseIds: remap(sw.useCaseIds) }));
        await updateDoc(typeRef, { name, description, useCases, [CORE_LINKS_FIELD]: coreLinks.map(linkDoc) });
        await Promise.all(
          software
            .filter((sw, i) => JSON.stringify(sw.useCaseIds) !== JSON.stringify(type.software[i].useCaseIds))
            .map((sw) => updateDoc(doc(typeRef, 'softwares', sw.id), { useCaseIds: sw.useCaseIds }))
        );
        const edited = { ...type, name, description, useCases, coreLinks, software };

        if (to === from) {
          setCollections((prev) => {
            const next = patchType(prev, from, m.id, () => edited);
            next[from] = { ...next[from], types: [...next[from].types].sort(compareTechTypes) };
            return next;
          });
        } else {
          // Move: same doc ids under the new tier (its slot order there starts fresh), then remove the old docs.
          // coreTools was just written, so the legacy coreSoftware field is dropped.
          const { order, coreSoftware, ...typeData } = (await getDoc(typeRef)).data() || {};
          const newTypeRef = doc(db, 'technologyStack', to, 'types', m.id);
          await setDoc(newTypeRef, typeData);
          const swSnap = await getDocs(collection(typeRef, 'softwares'));
          await Promise.all(swSnap.docs.map((s) => setDoc(doc(newTypeRef, 'softwares', s.id), { ...s.data(), collection: to })));
          await moveClientSelections(m.id, from, to);
          await Promise.all(swSnap.docs.map((s) => deleteDoc(s.ref)));
          await deleteDoc(typeRef);

          let next = {
            ...collections,
            [from]: { ...collections[from], types: collections[from].types.filter((t) => t.id !== m.id) },
            [to]: {
              ...collections[to],
              types: [...collections[to].types, { ...edited, order: undefined, software: software.map((sw) => ({ ...sw, collection: to })) }].sort(
                compareTechTypes
              ),
            },
          };
          // Leaving Core: other tiers can no longer offer it as "Your <tool>"
          if (from === 'Core') next = await stripCoreLinks([m.id], next);
          setCollections(next);
          setActiveTab(to);
        }
      }
      setTypeModal(null);
    } catch (error) {
      console.error('Error saving tool type:', error);
      alert(`Couldn't save the tool type: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteType = (c, type) =>
    setConfirm({
      title: `Delete ${type.name}?`,
      message: `This removes the ${type.name} tool type and its ${type.software.length} software option${type.software.length === 1 ? '' : 's'} for every client. This can't be undone.`,
      confirmLabel: 'Delete tool type',
      onConfirm: async () => {
        const typeRef = doc(db, 'technologyStack', c, 'types', type.id);
        await Promise.all(type.software.map((sw) => deleteDoc(doc(typeRef, 'softwares', sw.id))));
        await deleteDoc(typeRef);
        let next = { ...collections, [c]: { ...collections[c], types: collections[c].types.filter((t) => t.id !== type.id) } };
        if (c === 'Core') next = await stripCoreLinks([type.id], next);
        setCollections(next);
      },
    });

  /* ── Software ── */

  const openAddSoftware = (c = activeTab, typeId = '') => {
    setNewField({ fieldName: '', fieldDescription: '' });
    setSoftwareModal({ mode: 'add', draft: emptySoftwareDraft(c, typeId) });
  };

  const openEditSoftware = (c, type, sw) => {
    setNewField({ fieldName: '', fieldDescription: '' });
    setSoftwareModal({
      mode: 'edit',
      id: sw.id,
      origCollection: c,
      origTypeId: type.id,
      origUseCaseIds: sw.useCaseIds,
      draft: {
        name: sw.name || '',
        image: sw.image || '',
        signupUrl: sw.signupUrl || '',
        collection: c,
        typeId: type.id,
        useCaseIds: sw.useCaseIds || [],
        suggested: !!sw.suggested,
        open: !!sw.open,
        connectionFields: sw.connectionFields || [],
      },
    });
  };

  const setDraft = (patch) => setSoftwareModal((m) => ({ ...m, draft: { ...m.draft, ...patch } }));

  // Use case ids index into the type's list, so they only carry over on the same type.
  const setDraftLocation = (c, typeId) =>
    setSoftwareModal((m) => {
      const sameType = m.mode === 'edit' && c === m.origCollection && typeId === m.origTypeId;
      return { ...m, draft: { ...m.draft, collection: c, typeId, useCaseIds: sameType ? m.origUseCaseIds : [] } };
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (PNG, JPG, SVG or WebP).');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('That image is over 5 MB — please use a smaller logo.');
      return;
    }
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageRef = ref(storage, `tech-stack-software-images/${Date.now()}_${safeName}`);
      await uploadBytes(storageRef, file);
      setDraft({ image: await getDownloadURL(storageRef) });
    } catch (error) {
      console.error('Logo upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const saveSoftware = async () => {
    const m = softwareModal;
    const d = m.draft;
    const name = d.name.trim();
    if (!name || !d.typeId) return;
    const data = {
      name,
      image: d.image.trim(),
      signupUrl: d.signupUrl.trim(),
      typeId: d.typeId,
      collection: d.collection,
      useCaseIds: d.useCaseIds,
      suggested: d.suggested,
      open: d.open,
      connectionFields: d.connectionFields,
    };
    const softwaresCol = (c, typeId) => collection(db, 'technologyStack', c, 'types', typeId, 'softwares');
    const addTo = (prev, c, typeId, sw) =>
      patchType(prev, c, typeId, (t) => ({ ...t, software: [...t.software, sw].sort(compareTechSoftware) }));
    const removeFrom = (prev, c, typeId, id) =>
      patchType(prev, c, typeId, (t) => ({ ...t, software: t.software.filter((s) => s.id !== id) }));

    setSaving(true);
    try {
      if (m.mode === 'add') {
        const created = await addDoc(softwaresCol(d.collection, d.typeId), data);
        setCollections((prev) => addTo(prev, d.collection, d.typeId, normalizeSoftware(created.id, data)));
        setActiveTab(d.collection);
      } else if (d.collection === m.origCollection && d.typeId === m.origTypeId) {
        await updateDoc(doc(softwaresCol(d.collection, d.typeId), m.id), data);
        setCollections((prev) =>
          patchType(prev, d.collection, d.typeId, (t) => ({
            ...t,
            software: t.software.map((s) => (s.id === m.id ? normalizeSoftware(m.id, { ...s, ...data }) : s)).sort(compareTechSoftware),
          }))
        );
      } else {
        // Move: same doc id under the new tier/type, then remove the old doc.
        await setDoc(doc(softwaresCol(d.collection, d.typeId), m.id), data);
        await deleteDoc(doc(softwaresCol(m.origCollection, m.origTypeId), m.id));
        setCollections(addTo(removeFrom(collections, m.origCollection, m.origTypeId, m.id), d.collection, d.typeId, normalizeSoftware(m.id, data)));
        setActiveTab(d.collection);
      }
      setSoftwareModal(null);
    } catch (error) {
      console.error('Error saving software:', error);
      alert(`Couldn't save the software: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteSoftware = () => {
    const m = softwareModal;
    setConfirm({
      title: `Delete ${m.draft.name || 'this software'}?`,
      message: 'It stops being an option for every client. Clients who already picked it keep it as a custom entry.',
      confirmLabel: 'Delete software',
      onConfirm: async () => {
        await deleteDoc(doc(db, 'technologyStack', m.origCollection, 'types', m.origTypeId, 'softwares', m.id));
        setCollections((prev) => patchType(prev, m.origCollection, m.origTypeId, (t) => ({ ...t, software: t.software.filter((s) => s.id !== m.id) })));
        setSoftwareModal(null);
      },
    });
  };

  /* ── Reused Core software ── */

  const openCorePicker = (c, type) => setCorePicker({ collection: c, typeId: type.id, query: '', selected: [] });

  const addCoreLinks = async () => {
    const p = corePicker;
    const type = findType(p.collection, p.typeId);
    if (!type || !p.selected.length) return;
    setSaving(true);
    try {
      const added = p.selected.map((coreTypeId) => ({ coreTypeId, useCaseIds: [], suggested: false }));
      await writeLinks(p.collection, p.typeId, [...type.coreLinks, ...added]);
      setCorePicker(null);
    } catch (error) {
      console.error('Error adding Core tool:', error);
      alert(`Couldn't add the Core tool: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openLink = (c, type, sw) => {
    const link = type.coreLinks.find((l) => l.coreTypeId === sw.linkedCoreTypeId);
    if (link) setLinkModal({ collection: c, typeId: type.id, link: { ...link } });
  };

  const saveLink = async () => {
    const { collection: c, typeId, link } = linkModal;
    const type = findType(c, typeId);
    setSaving(true);
    try {
      await writeLinks(c, typeId, type.coreLinks.map((l) => (l.coreTypeId === link.coreTypeId ? link : l)));
      setLinkModal(null);
    } catch (error) {
      console.error('Error saving Core link:', error);
      alert(`Couldn't save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const removeLink = () => {
    const { collection: c, typeId, link } = linkModal;
    const type = findType(c, typeId);
    const coreType = coreTypesById.get(link.coreTypeId);
    setConfirm({
      title: `Remove "Your ${coreType?.name || 'Core tool'}" from ${type?.name}?`,
      message: `${coreType?.name || 'The tool'} stays in ${tierTitle('Core')} — it just stops being a ${type?.name} option.`,
      confirmLabel: 'Remove',
      onConfirm: async () => {
        await writeLinks(c, typeId, type.coreLinks.filter((l) => l.coreTypeId !== link.coreTypeId));
        setLinkModal(null);
      },
    });
  };

  const runConfirm = async () => {
    setSaving(true);
    try {
      await confirm.onConfirm();
      setConfirm(null);
    } catch (error) {
      console.error('Action failed:', error);
      alert(`Something went wrong: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  /* ── Render ── */

  const meta = TIER_META[activeTab];

  return (
    <div className="min-h-screen relative p-4 md:p-6 lg:p-8">
      <PortalBackground />
      <style>{pageStyles}</style>

      <div className="relative w-full">
        {/* Header */}
        <header className="techstack-rise grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-6">
          <div className="justify-self-start">
            <BackButton />
          </div>
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              <span aria-hidden="true">🗂️</span> Tech Stack Template
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
              The tool types and software options every client picks from on their pyramid
            </p>
          </div>
          <div className="justify-self-end">
            <button type="button" onClick={openAddType} className={BTN_PRIMARY}>
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Tool type</span>
            </button>
          </div>
        </header>

        {/* Tier switcher */}
        <div className="techstack-rise flex justify-center mb-6" style={{ animationDelay: '40ms' }}>
          <div className={`${GLASS} inline-flex flex-wrap justify-center gap-1.5 rounded-2xl p-1.5`}>
            {tierOrder.map((c) => {
              const active = c === activeTab;
              const m = TIER_META[c];
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveTab(c)}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    active ? m.active : 'text-gray-600 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-gray-800/70'
                  }`}
                >
                  <span aria-hidden="true">{m.emoji}</span>
                  {tierTitle(c)}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? m.count : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                    {collections[c].types.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="techstack-rise grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" style={{ animationDelay: '80ms' }}>
          {[
            { emoji: meta.emoji, label: 'Tool types', value: stats.types },
            { emoji: '📦', label: 'Software options', value: stats.software, hint: activeTab !== 'Core' && stats.fromCore ? `${stats.fromCore} linked from ${tierTitle('Core')}` : null },
            { emoji: '⭐', label: 'Suggested', value: stats.suggested },
            { emoji: '🔌', label: 'Open API', value: stats.open },
          ].map((s) => (
            <div key={s.label} className={`${GLASS} rounded-2xl p-4 flex flex-col items-center text-center`}>
              <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full ring-1 text-lg ${meta.chip}`} aria-hidden="true">{s.emoji}</span>
              <span className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{s.label}</span>
              <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{loading ? '–' : s.value}</span>
              {s.hint && <span className="text-xs text-gray-500 dark:text-gray-400">{s.hint}</span>}
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="techstack-rise flex flex-col sm:flex-row gap-3 mb-6" style={{ animationDelay: '120ms' }}>
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${tierTitle(activeTab)} tool types and software…`}
              className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-800/70 backdrop-blur text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400 transition"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <FilterSelect value={filter} onChange={(e) => setFilter(e.target.value)} className="w-full sm:w-56" aria-label="Filter software">
            {FILTERS.filter((f) => f.id !== 'core' || activeTab !== 'Core').map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </FilterSelect>
          <button type="button" onClick={() => openAddSoftware(activeTab)} className={BTN_PRIMARY}>
            <Plus className="w-4 h-4" />
            Software
          </button>
        </div>

        {/* Tool types */}
        {loading ? (
          <div className="flex justify-center py-24">
            <BBALoader size={96} />
          </div>
        ) : visibleTypes.length === 0 ? (
          <div className={`techstack-rise ${GLASS} rounded-3xl py-16 px-6 text-center`}>
            <div className="text-4xl mb-3" aria-hidden="true">{filtersActive ? '🔍' : meta.emoji}</div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {filtersActive ? 'Nothing matches' : `No ${tierTitle(activeTab)} tool types yet`}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {filtersActive ? 'Try a different search or filter.' : 'Add a tool type, then the software clients can choose for it.'}
            </p>
            <div className="mt-5 flex justify-center">
              {filtersActive ? (
                <button type="button" onClick={() => { setSearch(''); setFilter('all'); }} className={BTN_SECONDARY}>Clear filters</button>
              ) : (
                <button type="button" onClick={openAddType} className={BTN_PRIMARY}><Plus className="w-4 h-4" /> Tool type</button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {visibleTypes.map(({ type, options }, index) => (
              <section
                key={type.id}
                className={`techstack-rise ${GLASS} rounded-2xl p-5 md:p-6`}
                style={{ animationDelay: `${Math.min(index, 10) * 45 + 140}ms` }}
              >
                <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3">
                  <div className="justify-self-start pt-1">
                    <span className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/70 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                      {options.length} option{options.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="text-center max-w-2xl">
                    <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">{type.name}</h2>
                    {type.description ? (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{type.description}</p>
                    ) : (
                      <p className="mt-1 text-sm italic text-gray-400 dark:text-gray-500">No description</p>
                    )}
                  </div>
                  <div className="justify-self-end flex items-center gap-1">
                    <button type="button" onClick={() => openEditType(activeTab, type)} className={ICON_BTN} title="Edit tool type" aria-label={`Edit ${type.name}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteType(activeTab, type)}
                      className={`${ICON_BTN} hover:!text-red-600 dark:hover:!text-red-400`}
                      title="Delete tool type"
                      aria-label={`Delete ${type.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {type.useCases.length > 0 && (
                  <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                    {type.useCases.map((useCase, i) => (
                      <span key={i} className="rounded-full bg-gray-100/80 dark:bg-gray-800/80 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                        {useCase}
                      </span>
                    ))}
                  </div>
                )}

                {activeTab === 'Core' && (linkUsage.get(type.id) || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
                    <Link2 className="w-3.5 h-3.5" />
                    <span>Also offered as "Your {type.name}" on</span>
                    {linkUsage.get(type.id).map((u) => (
                      <span key={`${u.collection}-${u.typeId}`} className="rounded-full border border-blue-200 dark:border-blue-900/60 bg-blue-50/70 dark:bg-blue-950/30 px-2 py-0.5">
                        {TIER_META[u.collection].emoji} {u.typeName}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  {options.map((sw) => {
                    const useCaseNames = sw.useCaseIds.map((i) => type.useCases[parseInt(i, 10)]).filter(Boolean);
                    return (
                      <button
                        key={sw.id}
                        type="button"
                        onClick={() => (sw.fromCore ? openLink(activeTab, type, sw) : openEditSoftware(activeTab, type, sw))}
                        className={`techstack-tile group relative flex flex-col items-center text-center rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_30px_rgba(15,23,42,0.10)] dark:hover:shadow-[0_14px_30px_rgba(0,0,0,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/50 ${
                          sw.fromCore
                            ? 'border-dashed border-blue-300 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20'
                            : 'border-gray-200/80 dark:border-gray-700/70 bg-white/80 dark:bg-gray-900/70'
                        }`}
                      >
                        {sw.fromCore ? (
                          <div className="flex -space-x-3">
                            {sw.coreSoftware.slice(0, 3).map((coreSw) => (
                              <div key={coreSw.id} className="rounded-xl ring-2 ring-white dark:ring-gray-900">
                                <SoftwareLogo src={coreSw.image} name={coreSw.name} size="sm" />
                              </div>
                            ))}
                            {sw.coreSoftware.length === 0 && <SoftwareLogo name={sw.name.replace('Your ', '')} />}
                          </div>
                        ) : (
                          <SoftwareLogo src={sw.image} name={sw.name} />
                        )}
                        <span className="mt-2.5 text-sm font-semibold text-gray-900 dark:text-white line-clamp-2">{sw.name}</span>
                        {sw.fromCore && (
                          <span className="mt-0.5 text-[11px] text-blue-700/80 dark:text-blue-300/80">
                            Client's {tierTitle('Core')} pick{sw.pickName ? ` · default ${sw.pickName}` : ''}
                          </span>
                        )}
                        <div className="mt-2 flex flex-wrap justify-center gap-1">
                          {sw.suggested && <Badge tone="star"><Star className="w-3 h-3 fill-current" /> Suggested</Badge>}
                          {sw.open && !sw.fromCore && <Badge tone="open">🔌 Open API</Badge>}
                          {sw.fromCore && <Badge tone="core" title={`Linked to a ${tierTitle('Core')} tool`}><Link2 className="w-3 h-3" /> {tierTitle('Core')}</Badge>}
                        </div>
                        <span className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                          {useCaseNames.length ? `${useCaseNames.length} use case${useCaseNames.length === 1 ? '' : 's'}` : 'No use cases'}
                        </span>
                      </button>
                    );
                  })}

                  {!filtersActive && (
                    <button
                      type="button"
                      onClick={() => openAddSoftware(activeTab, type.id)}
                      className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-4 min-h-[9rem] text-gray-500 dark:text-gray-400 hover:border-red-300 hover:text-red-600 dark:hover:border-red-800 dark:hover:text-red-400 hover:bg-white/50 dark:hover:bg-gray-900/40 transition"
                    >
                      <Plus className="w-5 h-5" />
                      <span className="text-sm font-medium">Add software</span>
                    </button>
                  )}
                  {!filtersActive && activeTab !== 'Core' && (
                    <button
                      type="button"
                      onClick={() => openCorePicker(activeTab, type)}
                      className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-blue-200 dark:border-blue-900/70 p-4 min-h-[9rem] text-blue-600 dark:text-blue-400 hover:border-blue-400 dark:hover:border-blue-700 hover:bg-blue-50/50 dark:hover:bg-blue-950/30 transition"
                    >
                      <Link2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Add from {tierTitle('Core')}</span>
                      <span className="text-[11px] text-blue-500/80 dark:text-blue-400/70">e.g. their CRM</span>
                    </button>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* ── Tool type modal ── */}
      {typeModal && (
        <Modal
          emoji={TIER_META[typeModal.collection].emoji}
          title={typeModal.mode === 'add' ? 'New tool type' : `Edit ${typeModal.name || 'tool type'}`}
          subtitle={typeModal.mode === 'add' ? 'A slot on the client pyramid, like CRM or Scheduling' : `${tierTitle(typeModal.origCollection)} tier`}
          onClose={() => setTypeModal(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setTypeModal(null)} className={BTN_SECONDARY}>Cancel</button>
              <button type="button" onClick={saveType} disabled={saving || !typeModal.name.trim()} className={BTN_PRIMARY}>
                {saving
                  ? typeModal.mode === 'edit' && typeModal.collection !== typeModal.origCollection
                    ? 'Moving…'
                    : 'Saving…'
                  : typeModal.mode === 'add'
                    ? 'Create tool type'
                    : typeModal.collection !== typeModal.origCollection
                      ? `Save & move to ${tierTitle(typeModal.collection)}`
                      : 'Save changes'}
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <label className={LABEL_CLASS}>Name</label>
              <input
                type="text"
                value={typeModal.name}
                onChange={(e) => setTypeModal((m) => ({ ...m, name: e.target.value }))}
                placeholder="e.g. Scheduling"
                className={INPUT}
                autoFocus
              />
            </div>
            {(() => {
              const m = typeModal;
              const moving = m.mode === 'edit' && m.collection !== m.origCollection;
              const type = moving ? findType(m.origCollection, m.id) : null;
              const reusedBy = moving && m.origCollection === 'Core' ? linkUsage.get(m.id) || [] : [];
              return (
              <div>
                <label className={LABEL_CLASS}>Tier</label>
                <div className="grid grid-cols-3 gap-2">
                  {tierOrder.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setTypeModal((m) => ({ ...m, collection: c }))}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                        typeModal.collection === c
                          ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {TIER_META[c].emoji} {tierTitle(c)}
                    </button>
                  ))}
                </div>
                {moving && (
                  <div className="mt-3 flex gap-2.5 rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 text-xs text-blue-800 dark:text-blue-200">
                    <ArrowRightLeft className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p>
                        Moves {m.name.trim() || 'this tool type'} and its {type?.software.length || 0} software option
                        {type?.software.length === 1 ? '' : 's'} from {tierTitle(m.origCollection)} to {tierTitle(m.collection)}. Every client's pick for
                        it moves too.
                      </p>
                      {m.collection === 'Core' && type?.coreLinks.length > 0 && (
                        <p>
                          Its {type.coreLinks.length} linked {tierTitle('Core')} tool{type.coreLinks.length === 1 ? '' : 's'} will be dropped —{' '}
                          {tierTitle('Core')} types can't reuse {tierTitle('Core')}.
                        </p>
                      )}
                      {reusedBy.length > 0 && (
                        <p>
                          It stops being offered as “Your {m.name.trim() || type?.name}” on {reusedBy.map((u) => u.typeName).join(', ')}.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}
            <div>
              <label className={LABEL_CLASS}>Description</label>
              <textarea
                value={typeModal.description}
                onChange={(e) => setTypeModal((m) => ({ ...m, description: e.target.value }))}
                rows={3}
                placeholder="What this type of software is used for…"
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>Use cases</label>
              <p className="-mt-1 mb-2 text-xs text-gray-500 dark:text-gray-400">
                Software ticks these off. Renaming or removing one here keeps every software's ticks lined up.
              </p>
              <div className="space-y-2">
                {typeModal.rows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={row.text}
                      onChange={(e) =>
                        setTypeModal((m) => ({ ...m, rows: m.rows.map((r, j) => (j === i ? { ...r, text: e.target.value } : r)) }))
                      }
                      placeholder="e.g. Book appointments"
                      className={INPUT}
                    />
                    <button
                      type="button"
                      onClick={() => setTypeModal((m) => ({ ...m, rows: m.rows.filter((_, j) => j !== i) }))}
                      className={`${ICON_BTN} shrink-0 hover:!text-red-600`}
                      aria-label="Remove use case"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setTypeModal((m) => ({ ...m, rows: [...m.rows, { text: '', origIndex: null }] }))}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-red-300 hover:text-red-600 transition"
                >
                  <Plus className="w-4 h-4" /> Add use case
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Software modal ── */}
      {softwareModal && (() => {
        const m = softwareModal;
        const d = m.draft;
        const targetType = findType(d.collection, d.typeId);
        const moving = m.mode === 'edit' && (d.collection !== m.origCollection || d.typeId !== m.origTypeId);
        return (
          <Modal
            emoji={m.mode === 'add' ? '📦' : null}
            title={m.mode === 'add' ? 'Add software' : d.name || 'Edit software'}
            subtitle={m.mode === 'add' ? 'A choice clients can pick for a tool type' : `${tierTitle(m.origCollection)} · ${findType(m.origCollection, m.origTypeId)?.name || ''}`}
            onClose={() => setSoftwareModal(null)}
            width="max-w-3xl"
            footer={
              <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
                {m.mode === 'edit' && (
                  <button type="button" onClick={deleteSoftware} className={`${BTN_DANGER} sm:mr-auto`}>
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                )}
                <div className="flex gap-2 sm:ml-auto">
                  <button type="button" onClick={() => setSoftwareModal(null)} className={`${BTN_SECONDARY} flex-1 sm:flex-none`}>Cancel</button>
                  <button type="button" onClick={saveSoftware} disabled={saving || uploading || !d.name.trim() || !d.typeId} className={`${BTN_PRIMARY} flex-1 sm:flex-none`}>
                    {saving ? 'Saving…' : m.mode === 'add' ? 'Add software' : moving ? 'Save & move' : 'Save changes'}
                  </button>
                </div>
              </div>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-[15rem_1fr] gap-6">
              {/* Logo + flags */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-4 flex flex-col items-center text-center">
                  <SoftwareLogo src={d.image} name={d.name} size="lg" />
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className={`${BTN_SECONDARY} !px-3 !py-2`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                    {d.image && (
                      <button type="button" onClick={() => setDraft({ image: '' })} className={`${BTN_SECONDARY} !px-3 !py-2`}>
                        Remove
                      </button>
                    )}
                  </div>
                  <input
                    type="url"
                    value={d.image}
                    onChange={(e) => setDraft({ image: e.target.value })}
                    placeholder="…or paste an image URL"
                    className={`${INPUT} mt-3 text-xs`}
                  />
                  <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">Square logos look best (200×200+). Max 5 MB.</p>
                </div>
                <Toggle checked={d.suggested} onChange={(v) => setDraft({ suggested: v })} label="⭐ Suggested" hint="Starred and listed first for clients" />
                <Toggle checked={d.open} onChange={(v) => setDraft({ open: v })} label="🔌 Open API" hint="Can connect to other tools" />
              </div>

              {/* Details */}
              <div className="space-y-5">
                <div>
                  <label className={LABEL_CLASS}>Name</label>
                  <input type="text" value={d.name} onChange={(e) => setDraft({ name: e.target.value })} placeholder="e.g. Google Workspace" className={INPUT} autoFocus={m.mode === 'add'} />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Sign-up URL</label>
                  <div className="relative">
                    <input type="url" value={d.signupUrl} onChange={(e) => setDraft({ signupUrl: e.target.value })} placeholder="https://example.com/signup" className={`${INPUT} pr-10`} />
                    {d.signupUrl.trim() && (
                      <a href={d.signupUrl.trim()} target="_blank" rel="noopener noreferrer" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600" aria-label="Open sign-up link">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 p-4">
                  <div className="flex items-center justify-center gap-2 mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                    <ArrowRightLeft className="w-4 h-4 text-gray-400" /> Where it lives
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={LABEL_CLASS}>Collection</label>
                      <FilterSelect icon={false} value={d.collection} onChange={(e) => setDraftLocation(e.target.value, '')}>
                        {tierOrder.map((c) => <option key={c} value={c}>{TIER_META[c].emoji} {tierTitle(c)}</option>)}
                      </FilterSelect>
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Tool type</label>
                      <FilterSelect icon={false} value={d.typeId} onChange={(e) => setDraftLocation(d.collection, e.target.value)}>
                        <option value="">Select a tool type…</option>
                        {collections[d.collection].types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </FilterSelect>
                    </div>
                  </div>
                  {moving && (
                    <p className="mt-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                      Moving to {tierTitle(d.collection)}{targetType ? ` · ${targetType.name}` : ''}. Use cases reset for the new tool type, and clients who already picked it keep their pick on the old tool.
                    </p>
                  )}
                </div>

                <div>
                  <label className={LABEL_CLASS}>Use cases</label>
                  {targetType ? (
                    <UseCasePills
                      useCases={targetType.useCases}
                      selected={d.useCaseIds}
                      onToggle={(id) => setDraft({ useCaseIds: d.useCaseIds.includes(id) ? d.useCaseIds.filter((x) => x !== id) : [...d.useCaseIds, id] })}
                    />
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pick a tool type to choose use cases.</p>
                  )}
                </div>

                <div>
                  <label className={LABEL_CLASS}>Connection fields</label>
                  <p className="-mt-1 mb-2 text-xs text-gray-500 dark:text-gray-400">What a client needs to hand over to connect it (API key, username…).</p>
                  <div className="space-y-2">
                    {d.connectionFields.map((field, i) => (
                      <div key={field.id || i} className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 px-3.5 py-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{field.fieldName}</div>
                          {field.fieldDescription && <div className="text-xs text-gray-500 dark:text-gray-400">{field.fieldDescription}</div>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setDraft({ connectionFields: d.connectionFields.filter((_, j) => j !== i) })}
                          className="p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-white dark:hover:bg-gray-800"
                          aria-label={`Remove ${field.fieldName}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                      <input
                        type="text"
                        value={newField.fieldName}
                        onChange={(e) => setNewField((f) => ({ ...f, fieldName: e.target.value }))}
                        placeholder="Field name"
                        className={INPUT}
                      />
                      <input
                        type="text"
                        value={newField.fieldDescription}
                        onChange={(e) => setNewField((f) => ({ ...f, fieldDescription: e.target.value }))}
                        placeholder="Where to find it"
                        className={INPUT}
                      />
                      <button
                        type="button"
                        disabled={!newField.fieldName.trim()}
                        onClick={() => {
                          setDraft({
                            connectionFields: [
                              ...d.connectionFields,
                              { fieldName: newField.fieldName.trim(), fieldDescription: newField.fieldDescription.trim(), id: Date.now() },
                            ],
                          });
                          setNewField({ fieldName: '', fieldDescription: '' });
                        }}
                        className={BTN_SECONDARY}
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* ── Linked Core tool modal ── */}
      {linkModal && (() => {
        const type = findType(linkModal.collection, linkModal.typeId);
        const coreType = coreTypesById.get(linkModal.link.coreTypeId);
        if (!type || !coreType) return null;
        const link = linkModal.link;
        const setLink = (patch) => setLinkModal((lm) => ({ ...lm, link: { ...lm.link, ...patch } }));
        const pick = resolveCoreToolPick(coreType, null);
        return (
          <Modal
            emoji="🔗"
            title={`Your ${coreType.name}`}
            subtitle={`${TIER_META[linkModal.collection].emoji} ${type.name} option · linked to the ${tierTitle('Core')} ${coreType.name} tool`}
            onClose={() => setLinkModal(null)}
            footer={
              <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
                <button type="button" onClick={removeLink} className={`${BTN_DANGER} sm:mr-auto`}>
                  <Trash2 className="w-4 h-4" /> Remove from {type.name}
                </button>
                <div className="flex gap-2 sm:ml-auto">
                  <button type="button" onClick={() => setLinkModal(null)} className={`${BTN_SECONDARY} flex-1 sm:flex-none`}>Cancel</button>
                  <button type="button" onClick={saveLink} disabled={saving} className={`${BTN_PRIMARY} flex-1 sm:flex-none`}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
              </div>
            }
          >
            <div className="space-y-5">
              <div className="rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/20 px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
                <p>
                  On a client's pyramid this shows whatever they picked for <strong>{coreType.name}</strong> in {tierTitle('Core')} — a client on JobTread
                  sees JobTread here. If they haven't picked one yet it shows the suggested {coreType.name}
                  {pick ? <> (<strong>{pick.name}</strong>)</> : ' (none is suggested yet)'}.
                </p>
                {coreType.software.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {coreType.software.map((sw) => (
                      <span key={sw.id} className="inline-flex items-center gap-1.5 rounded-full bg-white/80 dark:bg-gray-900/60 border border-blue-200 dark:border-blue-900/60 pl-1 pr-2.5 py-0.5 text-xs">
                        <SoftwareLogo src={sw.image} name={sw.name} size="xs" />
                        {sw.name}{sw.suggested ? ' ⭐' : ''}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setLinkModal(null); setActiveTab('Core'); }}
                  className={`${BTN_SECONDARY} mt-3 !py-2`}
                >
                  <Pencil className="w-4 h-4" /> Manage {coreType.name} in {tierTitle('Core')}
                </button>
              </div>
              <div>
                <label className={LABEL_CLASS}>{type.name} use cases</label>
                <UseCasePills
                  useCases={type.useCases}
                  selected={link.useCaseIds}
                  onToggle={(id) => setLink({ useCaseIds: link.useCaseIds.includes(id) ? link.useCaseIds.filter((x) => x !== id) : [...link.useCaseIds, id] })}
                />
              </div>
              <Toggle checked={link.suggested} onChange={(v) => setLink({ suggested: v })} label={`⭐ Suggested for ${type.name}`} hint="Starred and listed first on this tool only" />
            </div>
          </Modal>
        );
      })()}

      {/* ── Add from Core picker ── */}
      {corePicker && (() => {
        const type = findType(corePicker.collection, corePicker.typeId);
        if (!type) return null;
        const linked = new Set(type.coreLinks.map((l) => l.coreTypeId));
        const q = corePicker.query.trim().toLowerCase();
        const coreTypes = collections.Core.types.filter(
          (coreType) => !q || coreType.name.toLowerCase().includes(q) || coreType.software.some((sw) => sw.name.toLowerCase().includes(q))
        );
        const toggle = (id) =>
          setCorePicker((p) => ({ ...p, selected: p.selected.includes(id) ? p.selected.filter((x) => x !== id) : [...p.selected, id] }));
        return (
          <Modal
            emoji="🔗"
            title={`Add a ${tierTitle('Core')} tool to ${type.name}`}
            subtitle="Clients can then pick whatever they already run for it — e.g. their CRM for Contracts"
            onClose={() => setCorePicker(null)}
            footer={
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCorePicker(null)} className={BTN_SECONDARY}>Cancel</button>
                <button type="button" onClick={addCoreLinks} disabled={saving || corePicker.selected.length === 0} className={BTN_PRIMARY}>
                  {saving ? 'Adding…' : corePicker.selected.length ? `Add ${corePicker.selected.length} to ${type.name}` : `Add to ${type.name}`}
                </button>
              </div>
            }
          >
            <div className="relative mb-4">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={corePicker.query}
                onChange={(e) => setCorePicker((p) => ({ ...p, query: e.target.value }))}
                placeholder={`Search ${tierTitle('Core')} tools…`}
                className={`${FIELD_CLASS} pl-10 pr-3 py-2.5`}
                autoFocus
              />
            </div>
            {coreTypes.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {q ? `No ${tierTitle('Core')} tool matches that search.` : `${tierTitle('Core')} has no tool types yet.`}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {coreTypes.map((coreType) => {
                  const already = linked.has(coreType.id);
                  const on = corePicker.selected.includes(coreType.id);
                  const pick = resolveCoreToolPick(coreType, null);
                  return (
                    <button
                      key={coreType.id}
                      type="button"
                      disabled={already}
                      onClick={() => toggle(coreType.id)}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                        already
                          ? 'border-gray-100 dark:border-gray-800 opacity-60 cursor-not-allowed'
                          : on
                            ? 'border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex -space-x-2 shrink-0">
                        {coreType.software.slice(0, 3).map((sw) => (
                          <div key={sw.id} className="rounded-lg ring-2 ring-white dark:ring-gray-900">
                            <SoftwareLogo src={sw.image} name={sw.name} size="sm" />
                          </div>
                        ))}
                        {coreType.software.length === 0 && <SoftwareLogo name={coreType.name} size="sm" />}
                      </div>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">Your {coreType.name}</span>
                        <span className="block truncate text-[11px] text-gray-500 dark:text-gray-400">
                          {already ? 'Already an option' : `${coreType.software.length} option${coreType.software.length === 1 ? '' : 's'}${pick ? ` · default ${pick.name}` : ''}`}
                        </span>
                      </span>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on || already ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                        {(on || already) && <Check className="w-3.5 h-3.5" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Modal>
        );
      })()}

      {/* ── Confirm ── */}
      {confirm && (
        <Modal
          emoji="⚠️"
          title={confirm.title}
          onClose={() => !saving && setConfirm(null)}
          width="max-w-md"
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirm(null)} disabled={saving} className={BTN_SECONDARY}>Cancel</button>
              <button type="button" onClick={runConfirm} disabled={saving} className={`${BTN_PRIMARY}`}>
                {saving ? 'Working…' : confirm.confirmLabel}
              </button>
            </div>
          }
        >
          <p className="text-center text-sm text-gray-600 dark:text-gray-300">{confirm.message}</p>
        </Modal>
      )}
    </div>
  );
};

export default TechStackTemplate;
